import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import MoneyUtils from '../../services/MoneyUtils';
import './style.css';
import Content from '../../constants/Content';

// Plan titles come through as words ("Six Month"); show the number ("6 Month").
const TERM_WORD_TO_NUMBER = {
  One: '1',
  Two: '2',
  Three: '3',
  Four: '4',
  Five: '5',
  Six: '6',
  Seven: '7',
  Eight: '8',
  Nine: '9',
  Ten: '10',
  Eleven: '11',
  Twelve: '12',
};

const getDisplayTitle = (title) => {
  const [firstWord, ...rest] = title.split(' ');
  if (TERM_WORD_TO_NUMBER[firstWord]) {
    return [TERM_WORD_TO_NUMBER[firstWord], ...rest].join(' ');
  }
  return title;
};

const getPlanAsClassName = (plan) => {
  let planClassName = `${plan.title}${plan.months}`;
  planClassName = planClassName.replace(/\s+/g, '');

  return planClassName;
};

// A single pricing card (plan mock layout): name, big price and its caption on a
// colour-filled header, then the value-prop subtitle and the CTA. The recommended plan renders as the filled/featured card with a badge.
// Cards flow in a swipe carousel on mobile and a centered row on desktop (both
// handled in select-subscription-boxes).
const SelectSubscription = (props) => {
  if (!props.plan) {
    return null;
  }

  const history = useHistory();

  let planInfoFromCoupon;
  if (props.subscriptionCouponsFromURL) {
    const subscriptionCouponFromURLThatMatchesThisPlan = props.subscriptionCouponsFromURL.find(
      (coupon) => {
        return coupon.exclusivePlans.find(
          (exclusivePlan) => exclusivePlan._id === props.plan._id,
        );
      },
    );
    if (subscriptionCouponFromURLThatMatchesThisPlan) {
      planInfoFromCoupon =
        subscriptionCouponFromURLThatMatchesThisPlan.planInfoOverride;
    }
  }

  const isFeatured = !!props.featured;

  const getSubContainerClass = (plan) => {
    return `subContainer subContainer${getPlanAsClassName(plan)}${
      isFeatured ? ' subContainer--featured' : ''
    }`;
  };

  const isInfinity = props.plan.months === 'INFINITY';
  const isMonthToMonth = props.plan.title === 'Month to Month';
  const isInfinityWithoutMonthToMonth = isInfinity && !isMonthToMonth;

  let discountPrice;
  if (props.referralCode && isInfinityWithoutMonthToMonth) {
    discountPrice = 0;
  } else if (planInfoFromCoupon?.costWithCoupon) {
    discountPrice = MoneyUtils.formatAsMoney(planInfoFromCoupon.costWithCoupon);
  } else {
    discountPrice = MoneyUtils.formatAsMoney(props.plan.costWithCoupon);
  }

  const discountPct = props.plan.couponToAutoPopulate?.discountPercentage;

  // Value-prop line under the plan name (mock: "No commitment" /
  // "First box 30% off + free gift"). Derived from plan fields.
  const getSubtitle = () => {
    if (isMonthToMonth) return 'No commitment';
    if (isInfinityWithoutMonthToMonth) {
      return discountPct
        ? `First box ${discountPct}% off + free gift`
        : 'Free gift on your first box';
    }
    return discountPct
      ? `${discountPct}% off + free gift`
      : 'Free gift at checkout';
  };

  // Small caption under the price (mock: "per month" / "first month, then $X").
  const getPriceCaption = () => {
    if (isMonthToMonth) return 'per month';
    if (isInfinityWithoutMonthToMonth) {
      return `first month, then $${props.plan.originalCost}/mo`;
    }
    return `delivered monthly for ${props.plan.months} months`;
  };

  const displayTitle = getDisplayTitle(props.plan.title);
  const subtitle = getSubtitle();
  const priceCaption = getPriceCaption();

  // Big dollars + small cents, matching the mock's price treatment.
  const [priceDollars, priceCents] = `${discountPrice}`.split('.');

  // The CTA routes to this plan's PDP (where the user reviews details + adds to
  // cart). Path map mirrors Content.defaultSubscriptionPdpUrlPath.
  const goToPdpPage = () => {
    if (props.plan.months !== 'INFINITY') {
      switch (props.plan.title) {
        case 'Six Month':
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath.prepaid[6]}`,
          );
          break;
        case 'Nine Month':
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath.prepaid[9]}`,
          );
          break;
        case 'Twelve Month':
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath.prepaid[12]}`,
          );
          break;
        default:
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath.prepaid[6]}`,
          );
          break;
      }
    } else {
      switch (props.plan.title) {
        case 'Three Month':
          history.push(`/products/${Content.defaultSubscriptionPdpUrlPath[3]}`);
          break;
        case 'Six Month':
          history.push(`/products/${Content.defaultSubscriptionPdpUrlPath[6]}`);
          break;
        case 'Nine Month':
          history.push(`/products/${Content.defaultSubscriptionPdpUrlPath[9]}`);
          break;
        case 'Twelve Month':
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath[12]}`,
          );
          break;
        case 'Month to Month':
          history.push(
            `/products/${Content.defaultSubscriptionPdpUrlPath.monthly}`,
          );
          break;
        default:
          history.push(`/products/${Content.defaultSubscriptionPdpUrlPath[6]}`);
          break;
      }
    }
  };

  return (
    <div className={getSubContainerClass(props.plan)}>
      {isFeatured ? (
        <div className='planCard__badge'>Most Popular</div>
      ) : null}
      {/* Header: name, price, and the price's own caption directly under it.
          The value-prop subtitle moved to the body, next to the CTA it supports. */}
      <div className='planCard__header'>
        <span className='planCard__heading'>
          <span className='planCard__name'>{displayTitle}</span>
        </span>
        <span className='planCard__headline-price'>
          <span className='planCard__amount'>
            <span className='planCard__amount-dollars'>${priceDollars}</span>
            {priceCents ? (
              <span className='planCard__amount-cents'>.{priceCents}</span>
            ) : null}
          </span>
        </span>
        {priceCaption ? (
          <p className='planCard__caption'>{priceCaption}</p>
        ) : null}
      </div>

      <div className='planCard__body'>
        {subtitle ? (
          <span className='planCard__subtitle'>{subtitle}</span>
        ) : null}
        <Button
          variant='primary'
          className='subSelectButton'
          onClick={goToPdpPage}
          aria-label={`View ${displayTitle} ${
            isInfinity ? '' : 'Bundled '
          }Subscription Plan details`}
        >
          Get Started
          <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
        </Button>
      </div>
    </div>
  );
};

export default SelectSubscription;
